/**
 * ProviderChangeCard - Card for medical provider entity changes.
 *
 * Displays provider name, status, treatment info, contacts, encounters,
 * and nested treatment requests.
 */

import { Building2, Mail, MapPin, Phone, Printer } from 'lucide-react';
import { useMemo, useState } from 'react';

import { BlobLink, ProviderServiceLink, TaskLink } from '@/components/entity-links/entities';
import { Badge } from '@/components/ui/catalyst/badge';
import type { EntityChangeSchema } from '@/services/api/generated/entityChangesService';

import { formatEnumValue } from '../utils/formatting';
import { useChangedFields } from '../utils/useChangedFields';
import { ChangedFieldHighlight } from './ChangedFieldHighlight';
import { NestedChangeIndicator } from './ChangeIndicators';
import { CollapsibleSection } from './CollapsibleSection';
import { useEntityChangeActions } from './EntityChangeActionsContext';
import { EntityChangeCardShell } from './EntityChangeCardShell';
import { type FieldConfig, KeyFieldsGrid, KeyFieldsGridUnchanged } from './KeyFieldsGrid';
import { ShowMoreSection } from './ShowMoreSection';
import { TreatmentRequestChangeCard } from './TreatmentRequestChangeCard';

const PRIMARY_FIELDS: FieldConfig[] = [
  { key: 'treatment_status', label: 'Treatment Status', isEnum: true },
  { key: 'still_treating', label: 'Still Treating' },
];

const DETAIL_FIELDS: FieldConfig[] = [
  { key: 'care_level', label: 'Care Level', isEnum: true },
  { key: 'treatment_frequency', label: 'Frequency', isEnum: true },
  { key: 'treatment_progress', label: 'Progress', isEnum: true },
  { key: 'pain_level', label: 'Pain Level' },
  { key: 'treatment', label: 'Treatment' },
  { key: 'dates_of_service', label: 'Dates of Service' },
  { key: 'treatment_status_occurred_at', label: 'Status Date', isDate: true },
  { key: 'date_of_records_request', label: 'Records Request Date', isDate: true },
];

const CONTACT_FIELDS: FieldConfig[] = [
  { key: 'phone', label: 'Phone' },
  { key: 'email', label: 'Email' },
  { key: 'fax', label: 'Fax' },
  { key: 'address', label: 'Address' },
];

const REFERENCE_FIELDS: FieldConfig[] = [
  { key: 'provider_contact_ref_id', label: 'Contact Ref ID' },
];

const HANDLE_ICONS: Record<string, React.ElementType> = {
  PHONE: Phone,
  EMAIL: Mail,
  FAX: Printer,
  MAILING_ADDRESS: MapPin,
};

interface ProviderChangeCardProps {
  entity: EntityChangeSchema;
  treatmentRequests?: EntityChangeSchema[];
  relatedTasks?: EntityChangeSchema[];
  matterId?: string;
}

export const ProviderChangeCard: React.FC<ProviderChangeCardProps> = ({
  entity,
  treatmentRequests = [],
  relatedTasks = [],
  matterId,
}) => {
  const changes = useChangedFields(
    entity.change_type,
    entity.changed_fields ?? [],
  );
  const actions = useEntityChangeActions();
  const effectiveStatus =
    actions?.statusOverrides[entity.id as string] ?? entity.status;

  const snapshot =
    entity.change_type === 'deleted' ? entity.old_snapshot : entity.new_snapshot;
  const data = (snapshot?.data ?? {}) as Record<string, unknown>;
  const providerName = (data.name as string) || 'Unknown Provider';
  const providerType = data.provider_type as string | undefined;
  const description = (data.description as string) || '';
  const handles = (data.handles as Record<string, unknown>[]) ?? [];
  const practitionerNames = (data.practitioner_names as string[]) ?? [];
  const treatmentNotes = (data.treatment_notes as string) || '';
  const [showRelatedWork, setShowRelatedWork] = useState(false);
  const providerId = entity.entity_id || (data.id as string | undefined) || '';

  const relatedFileCount = useMemo(
    () =>
      relatedTasks.reduce(
        (count, task) => {
          const taskSnapshot =
            task.change_type === 'deleted' ? task.old_snapshot : task.new_snapshot;
          const taskData = (taskSnapshot?.data ?? {}) as Record<string, unknown>;
          const attachments = (taskData.attachments as Record<string, unknown>[] | undefined) ?? [];
          return count + attachments.length;
        },
        0,
      ),
    [relatedTasks],
  );
  const shouldShowRelatedSection = Boolean(matterId && providerId);

  let relatedWorkBody: React.ReactNode = null;
  if (showRelatedWork) {
    if (relatedTasks.length === 0) {
      relatedWorkBody = (
        <p className="text-sm text-gray-500">
          No related tasks found for this provider.
        </p>
      );
    } else {
      relatedWorkBody = (
        <div className="space-y-2">
          {relatedTasks.map((task) => {
            const taskSnapshot =
              task.change_type === 'deleted' ? task.old_snapshot : task.new_snapshot;
            const taskData = (taskSnapshot?.data ?? {}) as Record<string, unknown>;
            const taskId = task.entity_id || (taskData.id as string | undefined) || '';
            const taskName = (taskData.name as string) || 'Task';
            const taskStatus = actions?.statusOverrides[task.id as string] ?? task.status;
            const attachments =
              (taskData.attachments as Record<string, unknown>[] | undefined) ?? [];

            return (
              <div key={task.id} className="rounded-md border border-gray-100 p-2">
                <div className="flex items-center justify-between gap-2">
                  {taskId ? (
                    <TaskLink
                      entityId={taskId}
                      matterId={matterId ?? ''}
                      className="text-sm font-medium"
                    >
                      {taskName}
                    </TaskLink>
                  ) : (
                    <span className="text-sm font-medium text-gray-900">{taskName}</span>
                  )}
                  <Badge color="zinc" className="text-xs">
                    {taskStatus}
                  </Badge>
                </div>

                {attachments.length ? (
                  <div className="mt-1 space-y-1 pl-2">
                    <div className="text-xs text-gray-400">Related Files</div>
                    {attachments.map((attachment, idx) => {
                      const attachmentId = attachment.id as string | undefined;
                      const attachmentName = (attachment.name as string | undefined) || 'File';

                      if (!attachmentId) {
                        return (
                          <div key={`${task.id}-attachment-${idx}`} className="text-sm text-gray-500">
                            {attachmentName}
                          </div>
                        );
                      }

                      return (
                        <div key={attachmentId} className="text-sm">
                          <BlobLink entityId={attachmentId} matterId={matterId ?? ''}>
                            {attachmentName}
                          </BlobLink>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="mt-1 text-xs text-gray-400">
                    No related files in snapshot
                  </div>
                )}
              </div>
            );
          })}
        </div>
      );
    }
  }

  // Auto-expand if there are nested treatment requests with actual changes
  const hasNestedChanges = treatmentRequests.some(
    (tr) => tr.change_type !== 'ignored',
  );
  const shouldExpand = hasNestedChanges ? true : undefined;

  return (
    <EntityChangeCardShell
      changeType={entity.change_type}
      name={providerName}
      entityStatus={effectiveStatus}
      defaultExpanded={shouldExpand}
      onStatusChange={
        actions
          ? (newStatus) => actions.onStatusChange(entity.id as string, newStatus)
          : undefined
      }
      description={description.length > 80 ? `${description.slice(0, 80)}…` : description}
      icon={Building2}
      iconBgClassName="bg-blue-50"
      iconTextClassName="text-blue-600"
      badges={
        providerType ? (
          <Badge
            color={providerType === 'ORGANIZATION' ? 'blue' : 'zinc'}
            className="text-xs"
          >
            {formatEnumValue(providerType)}
          </Badge>
        ) : undefined
      }
      changeCount={changes.totalChanges}
      hasExpandedContent
    >
      {/* ── Treatment Status ─────────────────────────── */}
      <KeyFieldsGrid
        fields={PRIMARY_FIELDS}
        data={data}
        changes={changes}
        changeType={entity.change_type}
      />

      {/* ── Treatment Details ─────────────────────────── */}
      <KeyFieldsGrid
        fields={DETAIL_FIELDS}
        data={data}
        changes={changes}
        changeType={entity.change_type}
      />

      {/* Treatment notes (if changed) */}
      {changes.isFieldChanged('treatment_notes') && treatmentNotes && (
        <ChangedFieldHighlight
          label="Treatment Notes"
          fieldKey="treatment_notes"
          changes={changes}
        >
          <p className="text-sm text-gray-900 whitespace-pre-wrap">
            {treatmentNotes}
          </p>
        </ChangedFieldHighlight>
      )}

      {/* ── People & Contact ──────────────────────────── */}
      {(practitionerNames.length > 0 || handles.length > 0) && (
        <>
          <div className="border-t border-gray-100" />
          {/* Practitioners inline */}
          {practitionerNames.length > 0 && (
            <div className="flex items-center gap-1.5 px-1 py-0.5 flex-wrap">
              <span className="text-xs text-gray-400 shrink-0">Practitioners:</span>
              {practitionerNames.map((name, i) => (
                <span key={i} className="inline-flex items-center gap-1">
                  <Badge color="zinc" className="text-xs">{name}</Badge>
                  <NestedChangeIndicator
                    prefix={`practitioner_names.${i}`}
                    changes={changes}
                  />
                </span>
              ))}
            </div>
          )}
          {/* Contact handles — collapsible to avoid clutter */}
          {handles.length > 0 && (
            <CollapsibleSection
              title="Contact Methods"
              count={handles.length}
              hasChanges={changes.hasChangesUnder('handles')}
            >
              <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
                {handles.map((handle, i) => {
                  const handleType = handle.handle_type as string;
                  const HandleIcon = HANDLE_ICONS[handleType] ?? Phone;
                  const value = (handle.value as string) || '';
                  const handleName = handle.name as string | undefined;
                  return (
                    <div
                      key={i}
                      className="flex items-center gap-1.5 px-1 py-0.5 text-sm text-gray-700"
                    >
                      <HandleIcon className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                      <span className="truncate">
                        {handleName ? `${handleName}: ` : ''}
                        {value}
                      </span>
                      <NestedChangeIndicator
                        prefix={`handles.${i}`}
                        changes={changes}
                      />
                    </div>
                  );
                })}
              </div>
            </CollapsibleSection>
          )}
          {/* Contact-level fields */}
          <KeyFieldsGrid
            fields={CONTACT_FIELDS}
            data={data}
            changes={changes}
            changeType={entity.change_type}
          />
        </>
      )}

      {/* ── Treatment Requests ────────────────────────── */}
      {treatmentRequests.length > 0 && (
        <>
          <div className="border-t border-gray-100" />
          <CollapsibleSection
            title="Treatment Requests"
            count={treatmentRequests.length}
            changeType={entity.change_type}
            hasChanges={treatmentRequests.some(
              (tr) => tr.change_type !== 'ignored',
            )}
          >
            <div className="space-y-2">
              {treatmentRequests.map((tr) => (
                <TreatmentRequestChangeCard key={tr.id} entity={tr} nested matterId={matterId} />
              ))}
            </div>
          </CollapsibleSection>
        </>
      )}

      {/* Entity links for references */}
      {typeof data.provider_service_ref_id === 'string' && data.provider_service_ref_id && (
        <div className="flex items-baseline gap-1.5 px-1 py-0.5">
          <span className="text-xs text-gray-400 shrink-0">Service Ref:</span>
          <ProviderServiceLink entityId={data.provider_service_ref_id} className="text-sm font-normal">
            Provider Service
          </ProviderServiceLink>
        </div>
      )}

      {/* Related tasks/files (Entity -> Task -> Related Files) */}
      {shouldShowRelatedSection && (
        <>
          <div className="border-t border-gray-100" />
          <button
            type="button"
            onClick={() => setShowRelatedWork((prev) => !prev)}
            className="flex items-center justify-between gap-2 text-xs font-medium text-gray-500 hover:text-gray-700 transition-colors w-full text-left py-1"
          >
            <span>
              {showRelatedWork ? 'Hide related tasks/files' : 'Show related tasks/files'}
            </span>
            {showRelatedWork && (
              <span className="text-gray-400">
                ({relatedTasks.length} tasks, {relatedFileCount} files)
              </span>
            )}
          </button>

          {showRelatedWork && <div className="mt-2 space-y-2 pl-1">{relatedWorkBody}</div>}
        </>
      )}

      <ShowMoreSection changeType={entity.change_type}>
        {!changes.isFieldChanged('treatment_notes') && treatmentNotes && (
          <div className="px-1 py-0.5">
            <span className="text-xs text-gray-400">Treatment Notes:</span>
            <p className="text-sm text-gray-700 whitespace-pre-wrap mt-0.5">
              {treatmentNotes}
            </p>
          </div>
        )}
        {!changes.isFieldChanged('description') && description && (
          <div className="px-1 py-0.5">
            <span className="text-xs text-gray-400">Description:</span>
            <p className="text-sm text-gray-700 whitespace-pre-wrap mt-0.5">
              {description}
            </p>
          </div>
        )}
        <KeyFieldsGridUnchanged
          fields={PRIMARY_FIELDS}
          data={data}
          changes={changes}
          changeType={entity.change_type}
        />
        <KeyFieldsGridUnchanged
          fields={DETAIL_FIELDS}
          data={data}
          changes={changes}
          changeType={entity.change_type}
        />
        <KeyFieldsGridUnchanged
          fields={CONTACT_FIELDS}
          data={data}
          changes={changes}
          changeType={entity.change_type}
        />
        <KeyFieldsGrid
          fields={REFERENCE_FIELDS}
          data={data}
          changes={changes}
          changeType={entity.change_type}
        />
        <KeyFieldsGridUnchanged
          fields={REFERENCE_FIELDS}
          data={data}
          changes={changes}
          changeType={entity.change_type}
        />
      </ShowMoreSection>
    </EntityChangeCardShell>
  );
};
